import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { UserService } from 'src/app/core/api/user/user.service';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { AuthenticationService } from 'src/app/core/services/authentication/authentication.service';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { DialogModalComponent } from 'src/app/shared/components/molecules/dialog-modal/dialog-modal.component';
import { LimitExceededException } from 'src/app/shared/errors/cognito/cognito-errors';
import { UserStatus } from 'src/app/shared/models/user/user-status';
import { RoutesKeys } from 'src/app/utils/routes';

@Component({
  templateUrl: './verification.page.html',
  styleUrls: ['./verification.page.scss'],
})
/**
 * Shall instantiate Verification template
 */
export class VerificationPage implements OnInit, OnDestroy {
  codeFormGroup!: UntypedFormGroup;
  // it must be discussed
  username?: string;
  successfulSent: boolean = true;
  successfulResent: boolean = true;
  codeCharNumber = 6;
  private dialogData!: any;

  private subsink = new Subscription();
  resendFeedback = {
    success: 'success-check-email',
    attempsLimited: 'error-change-password-limit-exceeded',
    fail: 'invalid-code',
  };

  readonly labels = {
    emailAlreadyVerifiedTitle: 'email-already-verified-dialog-title',
    emailAlreadyVerifiedContent: 'email-already-verified-dialog-content',
    successButtonText: 'ok',
  };

  /**
   * Declare the used services
   * @constructor
   */
  constructor(
    // NOSONAR
    private userService: UserService,
    private translationService: TranslationService,
    public dialog: MatDialog,
    private router: Router,
    private authenticationService: AuthenticationService,
    private authenticatedService: AuthenticatedUserService,
    private snackbarService: SnackBarService,
    private route: ActivatedRoute,
    private readonly dialogService: DialogService
  ) {}

  /**
   * Sets up form group and title
   */
  ngOnInit(): void {
    this.translationService.translateObject(this.resendFeedback);
    this.codeFormGroup = new FormGroup({
      code: new FormControl('', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(6),
      ]),
    });
    this.dialogData = {
      title: 'account-successfully-created-title',
      content: 'account-successfully-created-content',
      successButtonText: 'ok',
    };

    this.translationService.translateObject(this.dialogData);

    this.username =
      this.route.snapshot.queryParams['e'] ||
      this.authenticationService.passwordResetEmail ||
      this.authenticatedService.user?.email;

    if (!this.username) {
      const loginDialogData = {
        title: 'log-in-again-to-verify-title',
        content: 'log-in-again-to-verify-content',
        successButtonText: 'ok',
      };
      this.translationService.translateObject(loginDialogData);
      const dialogRef = this.dialog.open(DialogModalComponent, {
        data: loginDialogData,
        autoFocus: false,
      });
      this.subsink.add(
        dialogRef.afterClosed().subscribe(() => {
          this.router.navigate([RoutesKeys.LOGIN]);
        })
      );
    }

    if (this.authenticatedService.user?.status === UserStatus.LOGIN_COMPLETE) {
      this.router.navigate([RoutesKeys.HOME]);
    }
  }

  /**
   * Cancel subscriptions
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Action to take when submitting verification code
   */
  onSubmit() {
    this.subsink.add(
      this.userService
        .verifyCode(this.username!, this.codeFormGroup.controls['code'].value)
        .subscribe({
          next: () => {
            this.openDialog();
            this.successfulResent = true;
          },
          error: () => {
            this.codeFormGroup.controls['code'].setErrors({ wrongcode: true });
            this.successfulResent = true;
          },
        })
    );
  }

  /**
   *  Open confirmation dialog
   */
  private openDialog() {
    const dialogRef = this.dialog.open(DialogModalComponent, {
      data: this.dialogData,
      autoFocus: false,
    });

    this.subsink.add(
      dialogRef.afterClosed().subscribe(() => {
        this.router.navigate(['/login']).then(() => {
          window.location.reload();
        });
      })
    );
  }

  /**
   * Resend verification code
   */
  onResend() {
    this.subsink.add(
      this.userService.resendCode(this.username!).subscribe({
        next: () => {
          this.successfulResent = true;
          this.snackbarService.success(this.resendFeedback.success);
        },
        error: (err) => {
          this.successfulResent = false;
          if (err instanceof LimitExceededException) {
            return this.snackbarService.error(
              this.resendFeedback.attempsLimited
            );
          }
          if (
            err instanceof HttpErrorResponse &&
            err.error.errorCode === 'UMS-UAC-001'
          ) {
            this.openEmailAlreadyVerifiedDialog();
            return;
          }
          this.snackbarService.error(this.resendFeedback.fail);
        },
      })
    );
  }

  /**
   * Navigate to FAQ page and set a timeout to go to the contactUs section
   */
  navigateToFAQPage() {
    this.router.navigate(['/' + RoutesKeys.FAQ]).then(() => {
      setTimeout(() => {
        this.scrollToSection('contactUs');
      }, 500);
    });
  }

  /**
   *
   * @param {string} sectionId
   */
  private scrollToSection(sectionId: string) {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  /**
   * Gets code form control from our form group
   */
  get codeFormControl() {
    return this.codeFormGroup.controls['code'] as FormControl;
  }

  /**
   * Shows dialog informing user that the email was already verified.
   */
  private openEmailAlreadyVerifiedDialog() {
    const emailAlreadyVerifiedData = {
      title: this.labels.emailAlreadyVerifiedTitle,
      content: this.labels.emailAlreadyVerifiedContent,
      successButtonText: this.labels.successButtonText,
    };
    this.translationService.translateObject(emailAlreadyVerifiedData);

    this.dialogService.openDialog(
      emailAlreadyVerifiedData,
      () => {
        this.navigateToLoginPage();
      },
      () => {
        this.navigateToLoginPage();
      },
      600
    );
  }

  /**
   * Navigates to Login page.
   */
  private navigateToLoginPage() {
    this.router.navigate([RoutesKeys.LOGIN]);
  }
}
